import { Account, AccountSession } from "../models/user"

const cacheKey = "hg::cache::user"

// The cache nominally holds an AccountSession, but the post-payment redirect
// flow writes either a full Account or a bare { access_token } into the same
// slot. Reads only ever touch access_token / session_id / expires / is_signed_up.
type CachedUserPayload = AccountSession | Account | { access_token: string }

export type AccountType = "user" | "guest"

// The cached user is the single source of truth for who the session belongs
// to. Components read it through useSyncExternalStore, so every write has to
// notify - otherwise a guest session minted inside the fetch layer would not
// show up in the UI.
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

export const subscribeCachedUser = (listener: () => void) => {
  listeners.add(listener)
  // Another tab signing in or re-authing writes the same key; without this the
  // UI here would only catch up on its next own write
  const onStorage = (event: StorageEvent) => {
    if (event.key === cacheKey || event.key === null) {
      listener()
    }
  }
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage)
  }
  return () => {
    listeners.delete(listener)
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage)
    }
  }
}

export const setCachedUser = (user: CachedUserPayload) => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return
  }
  localStorage.setItem(cacheKey, JSON.stringify(user))
  notify()
}

export const removeCachedUser = () => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return
  }
  localStorage.removeItem(cacheKey)
  notify()
}

export const getCachedUser = ():
  | Partial<AccountSession>
  | { access_token: string }
  | Partial<Account>
  | null => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return null
  }
  const raw = localStorage.getItem(cacheKey)
  return raw
    ? (JSON.parse(raw) as
        | Partial<AccountSession>
        | { access_token: string }
        | Partial<Account>)
    : null
}

// A payload without is_signed_up (the bare { access_token } write) is a guest,
// matching what the old bootstrap effect concluded.
export const getCachedAccountType = (): AccountType => {
  const user = getCachedUser() as { is_signed_up?: boolean } | null
  return user?.is_signed_up ? "user" : "guest"
}

// Who account-dependent data (the venue's benefits and prices) is for. Every
// signed-in session is its own scope, so a different user signing in never
// sees the previous user's data; the cached session has no user id - its id
// is the token. All guests share one scope, so replacing one guest session
// with another keeps what was loaded.
export const getCachedSessionScope = (): string => {
  if (getCachedAccountType() !== "user") {
    return "guest"
  }
  return `user:${getCachedUser()?.access_token ?? ""}`
}

// Fired by the fetch layer when it refuses to replace a session (see On401
// "throw") - whichever call hit the 401: a query, a mutation, or a direct
// api call inside an event handler. Pages subscribe once instead of wiring
// every call site. `token` is the one the refused request was sent with, so
// a late refusal for a session that has since been replaced can be told apart.
export type SessionRefusedKind = AccountType

type SessionRefusedListener = (
  kind: SessionRefusedKind,
  token: string | null
) => void

const sessionRefusedListeners = new Set<SessionRefusedListener>()

export const subscribeSessionRefused = (listener: SessionRefusedListener) => {
  sessionRefusedListeners.add(listener)
  return () => {
    sessionRefusedListeners.delete(listener)
  }
}

export const notifySessionRefused = (
  kind: SessionRefusedKind,
  token: string | null
) => sessionRefusedListeners.forEach((listener) => listener(kind, token))
